export interface JjsProcessorSettings {
  enabled: boolean;
  title: string;
  description: string;
  /** Hosted checkout domain (default checkout2.jjsfinancial.net) */
  customDomain: string;
  /** USDC (Polygon) merchant wallet for payouts */
  walletAddress: string;
  /** Minimum fraction of expected payment to accept (0.60 = 40% fee tolerance) */
  tolerancePercentage: number;
  logoUrl?: string;
  backgroundColor?: string;
  themeColor?: string;
  buttonColor?: string;
}

export interface ProcessorConfig {
  jjs?: JjsProcessorSettings;
}

export const JJS_PROCESSOR_DESCRIPTION =
  "Use your credit or debit card as usual — your payment is securely processed and converted into USDC through our trusted partners. You'll be redirected to a secure external checkout to complete your purchase. Identity verification may be required for first-time users only and usually takes a few minutes. After completing payment you'll receive a confirmation by email with your order and invoice details.";

export const DEFAULT_JJS_PROCESSOR_SETTINGS: JjsProcessorSettings = {
  enabled: true,
  title: 'Pay By Credit / Debit Card',
  description: JJS_PROCESSOR_DESCRIPTION,
  customDomain: 'checkout2.jjsfinancial.net',
  walletAddress: '0xC70Dbc18115848fcD938E7F4fc1153fa42615B72',
  tolerancePercentage: 0.60,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

export function isJjsProcessorSettings(value: unknown): value is JjsProcessorSettings {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.enabled === 'boolean' &&
    typeof value.title === 'string' &&
    typeof value.description === 'string' &&
    typeof value.customDomain === 'string' &&
    typeof value.walletAddress === 'string' &&
    isFiniteNumber(value.tolerancePercentage) &&
    isOptionalString(value.logoUrl) &&
    isOptionalString(value.backgroundColor) &&
    isOptionalString(value.themeColor) &&
    isOptionalString(value.buttonColor)
  );
}

export function parseProcessorConfig(value: unknown): ProcessorConfig | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (!isRecord(value)) {
    return undefined;
  }

  const config: ProcessorConfig = {};

  if (value.jjs !== undefined) {
    if (!isJjsProcessorSettings(value.jjs)) {
      return undefined;
    }
    config.jjs = value.jjs;
  }

  return config;
}

export function resolveJjsProcessorSettings(
  config?: ProcessorConfig
): JjsProcessorSettings {
  const jjs = config?.jjs;
  if (!jjs) {
    return { ...DEFAULT_JJS_PROCESSOR_SETTINGS };
  }

  return {
    ...DEFAULT_JJS_PROCESSOR_SETTINGS,
    ...jjs,
  };
}
